/** project-board 응답 계약. 서버의 도메인 모델과 필드명을 맞춘다. */

export type StatusBucket = 'todo' | 'progress' | 'done' | 'dropped' | 'unknown';
export type StageFill = 'absent' | 'partial' | 'filled';
export type IssueStatus = 'todo' | 'progress' | 'done';
export type IncidentStatus = 'todo' | 'progress' | 'done';

export interface StageState {
  key: string;
  label: string;
  fill: StageFill;
  documents: { path: string; fill: 'absent' | 'template' | 'blocked' | 'filled' }[];
}

export interface StatusCounts {
  todo: number;
  progress: number;
  done: number;
  dropped: number;
  unknown: number;
}

/** 색인된 하네스 저장소 하나 — 화면의 "market / uncle / 전체" 구분이 이 목록에서 나온다. */
export interface BoardSource {
  key: string;
  commitSha: string | null;
  scannedAt: string;
}

/**
 * 재색인 범위 — 서버의 도메인 어휘를 그대로 쓴다. 화면에서 다른 말로 바꾸면 두 곳이 어긋난다.
 * `specs`는 화면에서 '에픽·스펙'으로 보이지만 키는 work-dir 종류 그대로다.
 */
export type ReindexScope = 'all' | 'specs' | 'issues' | 'incidents';

export interface ReindexRequested {
  scope: ReindexScope;
  /** 다시 읽을 하네스 저장소. `null`이면 전 저장소다. */
  source: string | null;
  requestedAt: string;
}

export interface IndexSettings {
  /** 색인 배치 주기 설명. 실제 주기는 저장소 밖(스케줄러)이 정하고 이 값은 그 거울이다. */
  schedule: string;
  /** 대기 중인 예약 전부. 저장소를 가리는 예약이라 여러 건이 함께 설 수 있다. */
  pending: ReindexRequested[];
}

export interface Epic {
  /** 어느 하네스 저장소에서 왔나 — slug는 저장소 안에서만 유일하다. */
  source: string;
  slug: string;
  path: string;
  year: string;
  month: string;
  title: string;
  summary: string;
  /** 담당자 — 기획 문서(PRD·HLD·lld·TASKS)를 만든 사람. */
  owner: string;
  counts: StatusCounts;
  activeCount: number;
  donePct: number;
  stages: StageState[];
  stage: string;
  stageLabel: string;
  gateStageLabel: string;
  /** 다음에 열어야 할 관문. 마지막 단계까지 채웠으면 null. */
  nextStageLabel: string | null;
  skippedStages: string[];
  documentPaths: string[];
  updatedAt: string | null;
  /** 이 스펙을 만들게 한 개발요청 번호. PRD의 `Dev request` 절에서 색인이 읽는다. 없으면 null. */
  devRequestId: number | null;
  /** QA 인계 요청서에 적힌 QA 일정(날짜는 `YYYY-MM-DD`). 요청서가 없거나 일정이 비었으면 `null`. */
  qaSchedule: QaSchedule | null;
}

export interface QaSchedule {
  reviewStart: string | null;
  reviewEnd: string | null;
  releaseTarget: string | null;
  note: string | null;
}

/**
 * 역기획서에 적을 QA 일정 입력. 빈 칸은 빈 문자열이고, 서버가 `미정`·`없음`으로 적는다.
 * 배포 목표일은 필수가 아니다 — 비면 `미정`으로 적히고, `releaseOwner`(확인 담당)를 적으면 `미정 — <담당>`이다.
 */
export interface QaScheduleDraft {
  reviewStart: string;
  reviewEnd: string;
  releaseTarget: string;
  releaseOwner: string;
  note: string;
}

export interface Task {
  uid: string;
  source: string;
  epicSlug: string;
  taskId: string;
  group: string;
  title: string;
  titleTag: string;
  titleHead: string;
  titleDetail: string;
  repo: string;
  owner: string;
  scope: string;
  estimate: string;
  estimateHours: number | null;
  dependsOn: string[];
  blockedBy: string[];
  status: StatusBucket;
  statusToken: string;
  statusNote: string;
  planStatus: string;
  progressStatus: string | null;
  worker: string;
  branch: string;
  updatedAt: string;
  notes: string;
  documentPath: string | null;
}

export interface Issue {
  source: string;
  id: string;
  path: string;
  slug: string;
  reportedAt: string;
  title: string;
  titleHead: string;
  titleDetail: string;
  summary: string;
  repos: string[];
  stages: StageState[];
  stage: string;
  stageLabel: string;
  gateStageLabel: string;
  skippedStages: string[];
  status: IssueStatus;
  /** 저장소 커밋 author 유래 — 이슈 문서에는 담당자 필드가 없다. */
  handledBy: string;
  handledAt: string | null;
  documentPaths: string[];
  updatedAt: string | null;
  /** nd-ops-record 사후 기록 마커로 판정 — AI-DART가 무인으로 조사·수정한 이슈인가. */
  aiDartHandled: boolean;
}

export interface Incident {
  source: string;
  id: string;
  path: string;
  slug: string;
  reportedAt: string;
  title: string;
  titleHead: string;
  titleDetail: string;
  summary: string;
  repos: string[];
  stages: StageState[];
  stage: string;
  stageLabel: string;
  gateStageLabel: string;
  skippedStages: string[];
  status: IncidentStatus;
  /** 저장소 커밋 author 유래 — 문서의 `담당` 메타 줄은 신뢰할 수 없어 이슈와 같은 방식으로 채운다. */
  handledBy: string;
  handledAt: string | null;
  documentPaths: string[];
  updatedAt: string | null;
}

export interface BoardIndex {
  generatedAt: string;
  sources: BoardSource[];
  epics: Epic[];
  tasks: Task[];
  issues: Issue[];
  incidents: Incident[];
  qaTickets: QaTicket[];
  facets: { owners: string[]; repos: string[] };
  stats: {
    epics: number;
    tasks: number;
    issues: number;
    openIssues: number;
    incidents: number;
    openIncidents: number;
  };
}

/** 색인기가 QA 산출물을 해석한 결과. 웹은 Markdown·HTML을 파싱하지 않는다. */
export type QaTicketStatus = 'No Run' | 'Pass' | 'Fail' | 'Block' | 'N/A' | 'Skip';

export interface QaTicket {
  source: string;
  epicSlug: string;
  documentPath: string;
  id: string;
  title: string;
  kind: '검증' | '관찰';
  priority: 'H' | 'M' | 'L';
  status: QaTicketStatus;
  details: { terminal: string; screen: string; why: string; criteria: string; source: string; excerpt: string; tcIds?: string[] };
  executions: Array<{ tcId: string; screen: string; precondition: string; steps: string; expected: string; memo: string }>;
}

export interface DocumentContent {
  source: string;
  path: string;
  text: string;
  updatedAt: string | null;
}

export const BUCKETS: ReadonlyArray<{ key: StatusBucket; label: string }> = [
  { key: 'todo', label: '할 일' },
  { key: 'progress', label: '진행 중' },
  { key: 'done', label: '완료' },
  { key: 'dropped', label: '제외' },
  { key: 'unknown', label: '미분류' },
];

export const ISSUE_STAGE_COLUMNS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'intake', label: '접수' },
  { key: 'investigation', label: '원인 조사' },
  { key: 'fix-plan', label: '수정 계획' },
  { key: 'execution', label: '수정' },
  { key: 'review', label: '리뷰' },
  { key: 'qa', label: 'QA · 완료' },
];

export const INCIDENT_STAGE_COLUMNS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'intake', label: '접수' },
  { key: 'doc-review', label: '문서 리뷰' },
  { key: 'tasks', label: 'task 분해' },
  { key: 'execution', label: '실행' },
  { key: 'review', label: '리뷰' },
  { key: 'qa', label: 'QA · 완료' },
];

/**
 * 에픽 필터용 단계 목록 — 서버 `FEATURE_STAGES`(project-board/core/domain/model/stage.ts)와 키·순서를
 * 맞춘다. 목록으로 그리지 않고 필터 드롭다운 후보로만 쓴다.
 */
export const EPIC_STAGE_COLUMNS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'prd', label: 'PRD' },
  { key: 'design', label: '설계(HLD/lld)' },
  { key: 'spec-review', label: '스펙 리뷰' },
  { key: 'tasks', label: 'task 분해' },
  { key: 'execution', label: '구현' },
  { key: 'review', label: '피처 리뷰' },
  { key: 'qa', label: 'QA' },
  { key: 'sync', label: '문서 동기화' },
];

/** 처리량 원천 하나의 분류 — AI-DART가 무인 처리한 이슈는 사람 처리량과 다른 줄로 그린다. */
export type ThroughputOrigin = 'general' | 'ai-dart';

/** 하루에 완료된 task·이슈 수(저장소별). 완료가 없던 날은 행이 없다. */
export interface DoneDay {
  source: string;
  /** 문서에 날짜가 없어 추세에 못 넣는 완료는 null로 모인다. */
  date: string | null;
  done: number;
  /** task는 항상 `general`이다 — AI-DART는 이슈만 무인으로 처리한다. */
  origin: ThroughputOrigin;
}

/** `GET /v1/board/throughput` 응답. */
export interface BoardThroughput {
  days: DoneDay[];
}

/**
 * 다른 팀이 슬랙으로 보낸 개발 요청 1건 — 하네스 색인이 아니라 **인입된 것이 정본**이다.
 * 보드의 다른 항목과 달리 저장소 문서에서 다시 만들 수 없다.
 */
export interface DevRequest {
  id: number;
  source: string;
  nativeId: string;
  title: string;
  body: string;
  /** 최초 접수자의 채널 식별자(슬랙 멤버 ID). 실명이 아니다. 개정이 와도 바뀌지 않는다. */
  requester: string;
  /** 마지막으로 개정을 보낸 사람. 개정이 없었으면 `null`. */
  reviser: string | null;
  channel: string | null;
  /** channel과 함께 답글을 달 좌표. 화면은 쓰지 않고, 나중에 스레드로 회신할 때 쓴다. */
  threadTs: string | null;
  /** 현재 버전을 만든 메시지의 슬랙 원문 주소. */
  permalink: string | null;
  attachments: DevRequestAttachment[];
  /** 1부터. 요청자가 고쳐 보낼 때마다 올라간다. */
  revision: number;
  /** 이 버전이 슬랙에 올라온 시각. */
  requestedAt: string;
  /** 처음 접수한 시각. 개정돼도 바뀌지 않는다. */
  receivedAt: string;
  updatedAt: string;
  /** 하네스 문서 폴더 이름이 된 영문 slug. 착수하면 `specs/<slug>/`로 이어진다. */
  slug: string;
  /** 이 요청을 맡은 사람. 콘솔에서 지정하며, 아직 지정 전이면 `null`이다. */
  assignee: string | null;
  /** 요청 문서가 적힌 하네스 저장소 키(`market`·`uncle`·`fo`). 처음 접수 때 정해진다. */
  platform: string;
}

/**
 * 그 개발요청을 가리키는 스펙 한 건.
 *
 * 요청 하나가 여러 스펙이 될 수 있어 목록으로 오고, 각 행이 `devRequestId`를 들고 있다 —
 * 화면이 그 값으로 묶는다.
 */
export interface DevRequestSpecLink {
  devRequestId: number;
  source: string;
  slug: string;
  title: string;
  /** work-dir 경로(`specs/2026/09/foo`). */
  path: string;
  /** 하네스 단계 위치 — 에픽 보드 카드가 서 있는 컬럼과 같은 값이다. */
  stage: string;
  stageLabel: string;
  donePct: number;
  updatedAt: string | null;
}

export interface DevRequestAttachment {
  name: string;
  mimetype: string;
  permalink: string;
}

/** 밀려난 이전 버전. 목록에는 없고 상세를 펼칠 때 따로 받는다(본문이 크다). */
export interface DevRequestRevision {
  revision: number;
  title: string;
  body: string;
  permalink: string | null;
  attachments: DevRequestAttachment[];
  requestedAt: string;
  archivedAt: string;
}

export interface EpicDevRequestLinkResult {
  outcome: 'completed' | 'incomplete';
  action: 'register' | 'link' | 'replace' | 'unlink';
  devRequestId: number | null;
  previousDevRequestId: number | null;
  requestDocumentPath: string | null;
  steps: Array<{ step: 'request' | 'request-document' | 'prd' | 'index'; outcome: 'done' | 'unchanged' | 'failed' | 'skipped' | 'deferred'; detail: string | null }>;
}
